var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import { getConfig, isServerSide } from "@jti/fe-common";
import { getMessageBus } from "@jti/fe-message-bus";
import { getUser as getUserData, LoyaltyApi, ReferralApi } from "@jti/rest-api";
import { GoogleTagManagerEvents } from "../../events";
import { addTrackingEventListener } from "../../helpers";
import { TrackingEvents } from "../../trackingEvents/types";
const log = debug("jti-aem:gtm");
const logCommerce = debug("jti-aem:gtm:commerce");
let instance = null;
export class BaseGTMRunner {
    constructor() {
        this.instance = null;
        this.isCartPage = false;
        this.isLoyaltyPage = false;
        this.gtmContainer = null;
        /**
         * @type {number}
         */
        this.points = 0;
        this.referee = "";
        this.currentName = "";
        this.promiseTimeoutInMs = 15000;
        this.config = getConfig();
        this.excludedRoutes = ["", "#/", "#/login"];
        this.messageBus = getMessageBus();
        if (!isServerSide()) {
            let currentHash = window === null || window === void 0 ? void 0 : window.location.hash;
            window.addEventListener("hashchange", () => {
                if (!this.isLoyaltyPage && !this.isCartPage && currentHash !== window.location.hash) {
                    currentHash = window.location.hash;
                    if (!this.excludedRoutes.includes(currentHash)) {
                        this.handleHashChange();
                    }
                }
            });
        }
    }
    handleHashChange() {
        if (this.gtmContainer) {
            this.currentName = this.getCurrentNameForGtm();
            this.instance = null;
            this.getCurrentDataObject();
        }
    }
    getCurrentNameForGtm() {
        var _a;
        if (isServerSide()) {
            return;
        }
        /*
         * TODO: think about automated solution
         * */
        const pageName = (_a = this.gtmContainer) === null || _a === void 0 ? void 0 : _a.dataset.pageName;
        const hash = window.location.hash;
        if (hash.length && !this.isLoyaltyPage) {
            const reactAppRoute = hash.replace("#/", "");
            const reactAppRoutes = {
                AGE_VERIFICATION: /^age-verification$/g,
                ORDERS: /^orders.*$/g,
                ACCOUNT: /^account$/g,
                ADDRESS: /^address.*$/g,
                PAYMENTS: /^payments$/g,
                DEVICES: /^devices$/g,
                COMMUNICATION: /^communication$/g,
                CHANGE_PASSWORD: /^change-password$/g,
                UPDATE_PHONE: /^update-phone$/g,
                UPDATE_PERSONAL_DETAILS: /^update-personal-details$/g,
                CHECKOUT: /^checkout$/g,
                FREETRIAL: /^trial\/checkout.*$/g,
                LOGIN: /^login$/g,
                REGISTER: /^register$/g,
                MY_SUBSCRIPTION: /^subscription$/g,
            };
            const gtmEventMap = {
                ROOT: this.isCartPage ? "cart" : "account-page",
                ACCOUNT: "account-information",
                ADDRESS: "my-addresses",
                CHECKOUT: "checkout",
                REGISTER: "signup_regular",
                COMMUNICATION: "communication-preferences",
                LOGIN: "login-page",
                ORDERS: "my-orders",
                FREETRIAL: "free-trial-checkout",
                DEVICES: "my-devices",
                MY_SUBSCRIPTION: "my-subscription",
            };
            const [keyFromRoute] = Object.entries(reactAppRoutes).find(([, regexp]) => regexp.test(reactAppRoute)) || [
                "ROOT",
            ];
            return gtmEventMap[keyFromRoute] || pageName;
        }
        return pageName;
    }
    getStandardData(name, callback) {
        if (!name || !this.instance) {
            return null;
        }
        const data = this.instance.getStandardData(name);
        callback === null || callback === void 0 ? void 0 : callback(data);
        return data;
    }
    trackActiveInstanceEvent(eventName, additionalData = {}) {
        var _a;
        try {
            const data = this.getStandardData(eventName);
            if ("dataLayer" in window && Array.isArray(window.dataLayer)) {
                log(`Tracking event ${eventName}${additionalData ? ` with data: ${JSON.stringify(additionalData)}` : ""}`);
                (_a = window.dataLayer) === null || _a === void 0 ? void 0 : _a.push(Object.assign(Object.assign({}, data), additionalData));
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    trackMessageBusEvents() {
        this.messageBus.subscribe({
            channel: "tracking.event",
            callback: ({ payload }) => {
                if (payload === null || payload === void 0 ? void 0 : payload.event_name) {
                    log(`MessageBus tracking event cought ${payload.event_name}${payload.additionalData ? ` with data: ${JSON.stringify(payload.additionalData)}` : ""}`);
                    this.trackActiveInstanceEvent(payload.event_name, payload.additionalData);
                }
            },
        });
    }
    runOnLoadTracking() {
        if (this.onLoadTracking && !this.isCartPage) {
            this.trackEvent(this.onLoadTracking, undefined);
        }
    }
    getCurrentDataObject() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.instance) {
                try {
                    const userData = getUserData();
                    if (userData.uuid) {
                        if (this.config.IS_LOYALTY_ENABLED === "true") {
                            const loyaltyApi = LoyaltyApi.init();
                            const loyaltyStatus = yield loyaltyApi.getStatus();
                            if (loyaltyStatus) {
                                const loyaltyPoints = yield loyaltyApi.getPoints(userData.uuid, userData.ciam_token, true);
                                this.points = (_a = loyaltyPoints === null || loyaltyPoints === void 0 ? void 0 : loyaltyPoints.points) !== null && _a !== void 0 ? _a : 0;
                            }
                        }
                        const referralApi = ReferralApi.init();
                        const isActive = yield referralApi.getStatus();
                        if (isActive) {
                            const rasCustomer = yield referralApi.getCustomer(userData.uuid, userData.ciam_token);
                            if (rasCustomer === null || rasCustomer === void 0 ? void 0 : rasCustomer.code) {
                                this.referee = (rasCustomer === null || rasCustomer === void 0 ? void 0 : rasCustomer.parent_code) ? "yes" : "no";
                            }
                        }
                    }
                }
                finally {
                    this.createInstance();
                }
            }
            return this.instance;
        });
    }
    createInstance() {
        this.instance = new GoogleTagManagerEvents(this.currentName || "other", () => {
            if (!isServerSide()) {
                sessionStorage.setItem("gtmInstance", "true");
                this === null || this === void 0 ? void 0 : this.runOnLoadTracking();
            }
        }, this.points, this.referee);
    }
    trackEvent(name, additionalData) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.waitForInstance(this.promiseTimeoutInMs);
                const data = this.getStandardData(name);
                if (!data) {
                    return;
                }
                const eventInfo = Object.assign(Object.assign({}, data), additionalData);
                if ("dataLayer" in window && Array.isArray(window.dataLayer)) {
                    log(`Tracking event ${name}${eventInfo ? ` with data: ${JSON.stringify(eventInfo)}` : ""}`);
                    window.dataLayer.push(eventInfo);
                }
            }
            catch (e) {
                log(`Error tracking event ${name}`);
                console.error(e);
            }
        });
    }
    waitForInstance(timeout) {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (this.instance) {
                    resolve(this.instance);
                    clearInterval(interval);
                }
            }, 500);
            setTimeout(() => {
                reject(new Error("GTM instance is unavailable"));
                clearInterval(interval);
            }, timeout);
        });
    }
    trackCommerceEvent(name, additionalData) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.waitForInstance(this.promiseTimeoutInMs);
                const data = this.getStandardData(name);
                if (!data) {
                    return;
                }
                const eventInfo = Object.assign(Object.assign({}, data), additionalData);
                logCommerce(`Tracking event ${name}${additionalData ? ` with data: ${JSON.stringify(additionalData)}` : ""}`);
                if ("dataLayer" in window && Array.isArray(window.dataLayer)) {
                    window.dataLayer.push({ ecommerce: null });
                    window.dataLayer.push(this.removeEmptyValues(eventInfo));
                }
            }
            catch (e) {
                console.error(e);
            }
        });
    }
    removeEmptyValues(data) {
        return Object.fromEntries(Object.entries(data).filter(([_, value]) => value !== null));
    }
    trackCustomEvents() {
        // Commerce Events
        addTrackingEventListener(TrackingEvents.TrackingOrderSummaryPurchase, (event) => {
            var _a;
            this.trackCommerceEvent("purchase", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCheckoutAddPaymentInfo, (event) => {
            var _a;
            this.trackCommerceEvent("add_payment_info", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCheckoutAddShippingInfo, (event) => {
            var _a;
            this.trackCommerceEvent("add_shipping_info", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCheckoutBeginCheckout, (event) => {
            var _a;
            this.trackCommerceEvent("begin_checkout", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCheckoutProgress, (event) => {
            var _a;
            this.trackCommerceEvent("checkout_progress", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCartOpen, (event) => {
            var _a;
            this.trackCommerceEvent("view_cart", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCartQuantityAdd, (event) => {
            var _a;
            this.trackCommerceEvent("add_to_cart", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCartQuantityRemove, (event) => {
            var _a;
            this.trackCommerceEvent("remove_from_cart", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingViewItemList, (event) => {
            var _a;
            this.trackCommerceEvent("view_item_list", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSelectItem, (event) => {
            var _a;
            this.trackCommerceEvent("select_item", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingViewItem, (event) => {
            var _a;
            this.trackCommerceEvent("view_item", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMyOrdersReorder, (event) => {
            var _a;
            this.trackCommerceEvent("re_order", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        // My Subscription Events
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionPause, () => {
            this.trackCommerceEvent("pause_subscription");
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionResume, () => {
            this.trackCommerceEvent("resume_subscription");
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionStop, () => {
            this.trackCommerceEvent("stop_subscription");
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionAddItem, (event) => {
            var _a;
            this.trackCommerceEvent("add_to_subscription", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionRemoveItem, (event) => {
            var _a;
            this.trackCommerceEvent("remove_item", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionIncreaseCarton, (event) => {
            var _a;
            this.trackCommerceEvent("increase_carton", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMySubscriptionDecreaseCarton, (event) => {
            var _a;
            this.trackCommerceEvent("decrease_carton", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        // Events
        addTrackingEventListener(TrackingEvents.TrackingShopFilterInteraction, (event) => {
            var _a;
            this.trackEvent("filter_interaction", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingUserAccountDeviceRegister, (event) => {
            var _a;
            this.trackEvent("device_register", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingUserAccountDeviceDeregister, (event) => {
            var _a;
            this.trackEvent("device_deregister", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingUserLoginDeviceRegister, (event) => {
            var _a;
            this.trackEvent("device_register", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCtaClick, (event) => {
            var _a;
            this.trackEvent("cta_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFaqLink, (event) => {
            var _a;
            this.trackEvent("faq_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFaqSearchClick, (event) => {
            var _a;
            this.trackEvent("faq_search", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFaqSearchResultClick, (event) => {
            var _a;
            this.trackEvent("faq_search_results_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSocialIconClick, (event) => {
            var _a;
            this.trackEvent("share_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingReferralCodeCopyButtonClick, (event) => {
            var _a;
            this.trackEvent("copy_a_code", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingPointsHistoryClick, (event) => {
            var _a;
            this.trackEvent("points_history_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCodeSubmitSuccess, (event) => {
            var _a;
            this.trackEvent("code_submit_success", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingListClick, (event) => {
            var _a;
            this.trackEvent("list_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingLoginSuccess, (event) => {
            var _a;
            this.trackEvent("login", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingLoginFail, (event) => {
            var _a;
            this.trackEvent("login_fail", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingLoginKakao, (event) => {
            var _a;
            this.trackEvent("kakao_login", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCreateAccountClick, (event) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield this.trackEvent("create_account_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        }));
        addTrackingEventListener(TrackingEvents.TrackingLogoutSuccess, (event) => {
            var _a;
            this.trackEvent("sign_out", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingEvent, (event) => {
            var _a, _b, _c, _d;
            if ((_b = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData) === null || _b === void 0 ? void 0 : _b.event) {
                this.trackEvent((_c = event.detail) === null || _c === void 0 ? void 0 : _c.additionalData.event, (_d = event.detail) === null || _d === void 0 ? void 0 : _d.additionalData);
            }
        });
        addTrackingEventListener(TrackingEvents.TrackingUnsubscribe, (event) => {
            var _a;
            this.trackEvent("unsubscribe", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFreeTrialStart, (event) => {
            var _a;
            this.trackEvent("free_trial_start", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSignUpRegular, (event) => {
            var _a;
            this.trackEvent("sign_up", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSignUpProgress, (event) => {
            var _a;
            this.trackEvent("sign_up_progress", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingRedeemClick, (event) => {
            var _a;
            this.trackEvent("redeem_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingRedeemCheckout, (event) => {
            var _a;
            this.trackEvent("redeem_checkout", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCheckoutPageView, (event) => {
            var _a;
            this.trackEvent("page_view", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFlavorFinderProgress, (event) => {
            var _a;
            this.trackEvent("flavor_finder_progress", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFlavorFinderResult, (event) => {
            var _a;
            this.trackEvent("flavor_finder_result", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.VideoClick, (event) => {
            var _a;
            this.trackEvent("video_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.VideoStop, (event) => {
            var _a;
            this.trackEvent("video_stop", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingHardAgeVerificationStart, (event) => {
            var _a;
            this.trackEvent("hav_start", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingHardAgeVerificationSuccess, (event) => {
            var _a;
            this.trackEvent("hav_success", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingFollow, (event) => {
            var _a;
            this.trackEvent("follow", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingContactUsClick, (event) => {
            var _a;
            this.trackEvent("contact_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingContactUsPhoneClick, (event) => {
            var _a;
            this.trackEvent("phone_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingBlogArticleClick, (event) => {
            var _a;
            this.trackEvent("article_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingStoreLocatorClick, (event) => {
            var _a;
            this.trackEvent("store_locator_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSubscribeBlockClick, (event) => {
            var _a;
            this.trackEvent("newsletter_subscription", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMenuNavigationClick, (event) => {
            var _a;
            this.trackEvent("menu_navigation", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingMenuNavigationListClick, (event) => {
            var _a;
            this.trackEvent("list_click", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingCarouselClick, (event) => {
            var _a;
            this.trackEvent("click_carousel", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingAgeVerificationClick, (event) => {
            var _a;
            this.trackEvent("age_verification", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSurveyStart, (event) => {
            var _a;
            this.trackEvent("survey_start", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSurveyProgress, (event) => {
            var _a;
            this.trackEvent("survey_progress", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
        addTrackingEventListener(TrackingEvents.TrackingSurveyCompletion, (event) => {
            var _a;
            this.trackEvent("survey_completion", (_a = event.detail) === null || _a === void 0 ? void 0 : _a.additionalData);
        });
    }
    static getInstance() {
        if (!instance) {
            instance = new BaseGTMRunner();
        }
        return instance;
    }
}
//# sourceMappingURL=BaseGTMRunner.js.map